import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface SegmentedOption {
  label: string;
  value: any;
  /** Optional badge after the label — e.g. how many rows the choice matches. */
  count?: number;
  /** Native tooltip, for a choice whose behaviour needs a word of explanation. */
  hint?: string;
}

/**
 * A row of mutually exclusive choices, styled as pills on a single track.
 *
 * Implemented as a radiogroup rather than a set of toggle buttons: it looks like
 * one control, so it behaves like one — a single tab stop, then arrow keys to
 * move between choices.
 */
@Component({
  selector: 'ks-segmented-control',
  standalone: false,
  templateUrl: './segmented-control.component.html',
  styleUrl: './segmented-control.component.scss',
})
export class SegmentedControlComponent {
  /** Sits inside the track, ahead of the choices. */
  @Input() label = '';
  @Input() options: SegmentedOption[] = [];
  @Input() value: any = null;
  /** Names the group for assistive tech when there is no visible label. */
  @Input() ariaLabel = '';
  /** Locks the control while the selection's consequence is still in flight. */
  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<any>();

  select(option: SegmentedOption): void {
    if (this.disabled || option.value === this.value) return;
    this.value = option.value;
    this.valueChange.emit(option.value);
  }

  /**
   * Roving tabindex: only the selected choice is reachable by Tab, so the group
   * costs one tab stop no matter how many choices it holds.
   */
  isTabbable(option: SegmentedOption, index: number): boolean {
    const selectedIndex = this.options.findIndex(item => item.value === this.value);
    return selectedIndex === -1 ? index === 0 : option.value === this.value;
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
      : 0;

    if (!step || this.disabled) return;
    event.preventDefault();

    const next = (index + step + this.options.length) % this.options.length;
    this.select(this.options[next]);
    this.focusOption(event, next);
  }

  private focusOption(event: KeyboardEvent, index: number): void {
    const group = (event.target as HTMLElement).parentElement;
    const options = group?.querySelectorAll<HTMLElement>('[role="radio"]');
    options?.item(index)?.focus();
  }
}
